import { useEffect, useState } from 'react'
import { Bell, Menu, Moon, Music, Pause, Sparkles, Sun, User as UserIcon, ZapOff } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../modules/auth/hooks/useAuth'
import { useToast } from '../lib/toast'
import UserAvatar from './common/UserAvatar'

type HeaderProps = {
  onOpenSidebar?: () => void
  onOpenAuth?: () => void
}

export default function Header({ onOpenSidebar, onOpenAuth }: HeaderProps) {
  const navigate = useNavigate()
  const { user, signOut, isInitializing } = useAuth()
  const { toggleNotifications, unreadCount } = useToast()
  const [isMusicOn, setIsMusicOn] = useState(false)
  const [isEffectsOn, setIsEffectsOn] = useState(false)
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'))

  useEffect(() => {
    // Effects check
    const storedEffects = window.localStorage.getItem('digigo-effects')
    if (storedEffects === 'on') setIsEffectsOn(true)

    const onEffectsChanged = (ev: Event) => {
      const isOn = (ev as CustomEvent<{ isEffectsOn: boolean }>).detail?.isEffectsOn
      if (typeof isOn === 'boolean') setIsEffectsOn(isOn)
    }
    window.addEventListener('digigo-effects-changed', onEffectsChanged)
    return () => window.removeEventListener('digigo-effects-changed', onEffectsChanged)
  }, [])

  useEffect(() => {
    const onMusicChanged = (ev: Event) => {
      const next = (ev as CustomEvent<{ isMusicOn: boolean }>).detail?.isMusicOn
      if (typeof next === 'boolean') setIsMusicOn(next)
    }
    window.addEventListener('digigo-music-changed', onMusicChanged)
    return () => window.removeEventListener('digigo-music-changed', onMusicChanged)
  }, [])

  useEffect(() => {
    const onThemeChanged = (ev: Event) => {
      const nextIsDark = (ev as CustomEvent<{ isDark: boolean }>).detail?.isDark
      if (typeof nextIsDark === 'boolean') setIsDark(nextIsDark)
    }
    window.addEventListener('digigo-theme-changed', onThemeChanged)
    return () => window.removeEventListener('digigo-theme-changed', onThemeChanged)
  }, [])



  const toggleMusic = () => {
    window.dispatchEvent(new Event('digigo-music-toggle'))
  }

  const toggleTheme = () => {
    const next = !isDark
    window.localStorage.setItem('digigo-theme', next ? 'dark' : 'light')
    document.documentElement.classList.toggle('dark', next)
    window.dispatchEvent(new CustomEvent('digigo-theme-changed', { detail: { isDark: next } }))
    setIsDark(next)
  }

  const toggleEffects = () => {
    const next = !isEffectsOn
    window.localStorage.setItem('digigo-effects', next ? 'on' : 'off')
    window.dispatchEvent(new CustomEvent('digigo-effects-changed', { detail: { isEffectsOn: next } }))
    setIsEffectsOn(next)
  }

  return (
    <header className="md:hidden topbar-surface sticky top-0 z-50 transition-all duration-300">
      <div className="mx-auto px-3 sm:px-4">
        <div className="h-14 flex items-center gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
            <div className="flex items-center gap-3 cursor-pointer min-w-0" onClick={() => navigate('/products')}>
              <img
                src="/DigiGO.png"
                alt="Tạp hóa nội dung số"
                className="h-8 w-8 rounded-xl object-cover ring-1 ring-white/30 shadow-sm"
              />
              <div className="min-w-0 hidden xs:block">
                <div className="text-base font-extrabold tracking-tight text-slate-900 dark:text-white truncate">
                  Tạp hóa nội dung số
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={onOpenSidebar}
              className="icon-btn"
              aria-label="Mở mục lục"
              title="Mục lục"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ml-auto">
            <button
              type="button"
              onClick={toggleTheme}
              className="icon-btn"
              title={isDark ? 'Chế độ sáng' : 'Chế độ tối'}
              aria-label={isDark ? 'Chế độ sáng' : 'Chế độ tối'}
            >
              {isDark ? <Sun className="h-5 w-5 text-amber-400" /> : <Moon className="h-5 w-5 text-slate-600" />}
            </button>

            <button
              type="button"
              onClick={toggleMusic}
              className="hidden xs:grid icon-btn"
              title={isMusicOn ? 'Tắt nhạc nền' : 'Bật nhạc nền'}
              aria-label={isMusicOn ? 'Tắt nhạc nền' : 'Bật nhạc nền'}
            >
              {isMusicOn ? <Pause className="h-5 w-5 text-emerald-500" /> : <Music className="h-5 w-5" />}
            </button>

            <button
              type="button"
              onClick={toggleEffects}
              className="icon-btn"
              title={isEffectsOn ? 'Tắt hiệu ứng' : 'Bật hiệu ứng'}
              aria-label={isEffectsOn ? 'Tắt hiệu ứng' : 'Bật hiệu ứng'}
            >
              {isEffectsOn ? <Sparkles className="h-5 w-5 text-purple-500" /> : <ZapOff className="h-5 w-5 text-slate-400" />}
            </button>

            <button
              type="button"
              onClick={toggleNotifications}
              className="relative icon-btn"
              title="Thông báo"
              aria-label="Thông báo"
            >
              <Bell className="h-5 w-5 text-amber-500" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => {
                if (user) {
                  signOut()
                } else {
                  onOpenAuth?.()
                }
              }}
              className="btn-ghost flex items-center gap-2 border-amber-200 dark:border-amber-400/20 bg-amber-50/50 dark:bg-amber-400/5 hover:bg-amber-100 dark:hover:bg-amber-400/10"
              title={user ? 'Đăng xuất' : 'Xác thực'}
              disabled={isInitializing}
            >
              {user ? (
                <>
                  <UserAvatar
                    src={user.avatar_url}
                    name={user.full_name || user.username || user.email}
                    alt={user.full_name || user.username || 'Avatar người dùng'}
                    containerClassName="h-5 w-5 rounded-md border border-amber-200 dark:border-amber-400/20"
                    iconClassName="text-[10px]"
                  />
                  <span className="hidden sm:inline">Thoát</span>
                </>
              ) : (
                <>
                  <UserIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                  <span className="hidden sm:inline">Xác thực</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

    </header>
  )
}
